import {
  Button,
  Flex,
  Grid,
  SelectField,
  TextAreaField,
  TextField,
  CheckboxField,
} from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import LocalizedStrings from "react-localization";

const FAKE_FARMS = [
  { id: "farm-001", name: "Finca Las Palmas" },
  { id: "farm-002", name: "Finca El Roble" },
  { id: "farm-003", name: "Finca La Esperanza" },
  { id: "farm-004", name: "Finca San José" },
  { id: "farm-005", name: "Finca Santa Rita" },
];

// sample equipments (only sprayers included)
const FAKE_EQUIPMENTS = [
  {
    id: "eq-spr-001",
    name: "Pulverizador 200L",
    theoreticalNozzleFlowCcPerMin: 120,
  },
  {
    id: "eq-spr-002",
    name: "Pulverizador 400L",
    theoreticalNozzleFlowCcPerMin: 130,
  },
  {
    id: "eq-spr-003",
    name: "Pulverizador de mochila 16L",
    theoreticalNozzleFlowCcPerMin: 40,
  },
  {
    id: "eq-spr-004",
    name: "Pulverizador de barra 600L",
    theoreticalNozzleFlowCcPerMin: 140,
  },
  {
    id: "eq-spr-005",
    name: "Pulverizador portátil 50L",
    theoreticalNozzleFlowCcPerMin: 60,
  },
];

const FAKE_SERIALS_BY_EQUIPMENT = {
  "eq-spr-001": [
    "SN-PS200-001",
    "SN-PS200-002",
    "SN-PS200-003",
    "SN-PS200-004",
    "SN-PS200-005",
  ],
  "eq-spr-002": [
    "SN-PS400-101",
    "SN-PS400-102",
    "SN-PS400-103",
    "SN-PS400-104",
    "SN-PS400-105",
  ],
  "eq-spr-003": [
    "SN-MK16-201",
    "SN-MK16-202",
    "SN-MK16-203",
    "SN-MK16-204",
    "SN-MK16-205",
  ],
  "eq-spr-004": [
    "SN-BAR600-301",
    "SN-BAR600-302",
    "SN-BAR600-303",
    "SN-BAR600-304",
    "SN-BAR600-305",
  ],
  "eq-spr-005": [
    "SN-PT50-401",
    "SN-PT50-402",
    "SN-PT50-403",
    "SN-PT50-404",
    "SN-PT50-405",
  ],
};

const FAKE_COLLABORATORS = [
  { id: "col-0001", name: "Juan Pérez" },
  { id: "col-0002", name: "María Gómez" },
  { id: "col-0003", name: "Carlos Rojas" },
  { id: "col-0004", name: "Ana Martínez" },
  { id: "col-0005", name: "Luis Díaz" },
];

const NOZZLE_CONDITIONS = ["", "New", "Used"];

const EquipmentCalibrationForm = ({
  onSubmit,
  type = "create",
  initialValues = {},
  onCancel,
}) => {
  const defaultState = {
    date: "",
    farmId: "",
    farmName: "",
    equipmentId: "",
    equipmentName: "",
    serialNumber: "",
    theoreticalNozzleFlowCcPerMin: "",
    actualNozzleFlowCcPerMin: "",
    registers: { T1: "", T2: "", T3: "", average: "" },
    nozzleCondition: "",
    nozzleChange: false,
    performedById: "",
    performedByName: "",
    photoEvidence: "",
    observations: "",
    createdAt: "",
  };

  const [form, setForm] = useState({ ...defaultState, ...initialValues });
  const [availableSerials, setAvailableSerials] = useState([]);

  useEffect(() => {
    setForm({ ...defaultState, ...initialValues });

    if (initialValues.equipmentId) {
      const serials =
        FAKE_SERIALS_BY_EQUIPMENT[initialValues.equipmentId] || [];
      setAvailableSerials(serials);
      const eq = FAKE_EQUIPMENTS.find(
        (e) => e.id === initialValues.equipmentId
      );
      if (eq && !initialValues.equipmentName)
        setForm((f) => ({ ...f, equipmentName: eq.name }));
      if (eq && !initialValues.theoreticalNozzleFlowCcPerMin)
        setForm((f) => ({
          ...f,
          theoreticalNozzleFlowCcPerMin: eq.theoreticalNozzleFlowCcPerMin,
        }));
    }

    if (initialValues.performedById && !initialValues.performedByName) {
      const found = FAKE_COLLABORATORS.find(
        (c) => c.id === initialValues.performedById
      );
      if (found) setForm((f) => ({ ...f, performedByName: found.name }));
    }

    // compute average if registers provided
    if (
      initialValues.registers &&
      typeof initialValues.registers === "object"
    ) {
      const regs = initialValues.registers;
      const nums = ["T1", "T2", "T3"].map((k) => Number(regs[k]) || 0);
      const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
      setForm((f) => ({
        ...f,
        registers: { ...regs, average: Number(avg.toFixed(2)) },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleEquipmentChange = (equipmentId) => {
    const eq = FAKE_EQUIPMENTS.find((e) => e.id === equipmentId);
    const serials = FAKE_SERIALS_BY_EQUIPMENT[equipmentId] || [];
    setAvailableSerials(serials);
    setForm({
      ...form,
      equipmentId,
      equipmentName: eq ? eq.name : "",
      serialNumber: "",
      theoreticalNozzleFlowCcPerMin: eq ? eq.theoreticalNozzleFlowCcPerMin : "",
    });
  };

  const handleRegisterChange = (key, value) => {
    const regs = { ...form.registers, [key]: value };
    const nums = ["T1", "T2", "T3"].map((k) => Number(regs[k]) || 0);
    const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
    regs.average = Number(avg.toFixed(2));
    setForm({
      ...form,
      registers: regs,
      actualNozzleFlowCcPerMin: regs.average,
    });
  };

  const onSubmitForm = () => {
    const payload = { ...form };
    // ensure average computed
    const nums = ["T1", "T2", "T3"].map(
      (k) => Number(payload.registers[k]) || 0
    );
    payload.registers.average = Number(
      (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(2)
    );
    payload.actualNozzleFlowCcPerMin = payload.registers.average;
    if (!payload.createdAt) payload.createdAt = new Date().toISOString();
    onSubmit && onSubmit(payload);
  };

  return (
    <Grid rowGap="12px" columnGap="12px" padding="20px">
      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="date"
          label={strings.fields.date.label}
          placeholder="YYYY-MM-DD"
          value={form.date || ""}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
        />

        <SelectField
          name="farmId"
          label={strings.fields.farmId.label}
          value={form.farmId || ""}
          onChange={(e) => setForm({ ...form, farmId: e.target.value })}
        >
          <option value="">--</option>
          {FAKE_FARMS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="equipmentId"
          label={strings.fields.equipmentId.label}
          value={form.equipmentId || ""}
          onChange={(e) => handleEquipmentChange(e.target.value)}
        >
          <option value="">--</option>
          {FAKE_EQUIPMENTS.map((eq) => (
            <option key={eq.id} value={eq.id}>
              {eq.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          name="serialNumber"
          label={strings.fields.serialNumber.label}
          value={form.serialNumber || ""}
          onChange={(e) => setForm({ ...form, serialNumber: e.target.value })}
        >
          <option value="">--</option>
          {availableSerials.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="theoreticalNozzleFlowCcPerMin"
          label={strings.fields.theoreticalNozzleFlow.label}
          value={form.theoreticalNozzleFlowCcPerMin || ""}
          onChange={(e) =>
            setForm({ ...form, theoreticalNozzleFlowCcPerMin: e.target.value })
          }
        />
        <TextField
          name="actualNozzleFlowCcPerMin"
          label={strings.fields.actualNozzleFlow.label}
          value={form.actualNozzleFlowCcPerMin || ""}
          onChange={(e) =>
            setForm({ ...form, actualNozzleFlowCcPerMin: e.target.value })
          }
        />
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="T1"
          label="Registro T1 (cc/min)"
          value={form.registers.T1 || ""}
          onChange={(e) => handleRegisterChange("T1", e.target.value)}
        />
        <TextField
          name="T2"
          label="Registro T2 (cc/min)"
          value={form.registers.T2 || ""}
          onChange={(e) => handleRegisterChange("T2", e.target.value)}
        />
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="T3"
          label="Registro T3 (cc/min)"
          value={form.registers.T3 || ""}
          onChange={(e) => handleRegisterChange("T3", e.target.value)}
        />
        <TextField
          name="average"
          label="Promedio (cc/min)"
          value={form.registers.average || ""}
          isReadOnly
        />
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="nozzleCondition"
          label={strings.fields.nozzleCondition.label}
          value={form.nozzleCondition || ""}
          onChange={(e) =>
            setForm({ ...form, nozzleCondition: e.target.value })
          }
        >
          {NOZZLE_CONDITIONS.map((c) => (
            <option key={c} value={c}>
              {c || "--"}
            </option>
          ))}
        </SelectField>

        <CheckboxField
          name="nozzleChange"
          label={strings.fields.nozzleChange.label}
          isChecked={!!form.nozzleChange}
          onChange={(e) => setForm({ ...form, nozzleChange: e.target.checked })}
        />
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="performedById"
          label={strings.fields.performedById.label}
          value={form.performedById || ""}
          onChange={(e) => {
            const val = e.target.value;
            const found = FAKE_COLLABORATORS.find((c) => c.id === val);
            setForm({
              ...form,
              performedById: val,
              performedByName: found ? found.name : "",
            });
          }}
        >
          <option value="">--</option>
          {FAKE_COLLABORATORS.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </SelectField>

        <TextField
          name="photoEvidence"
          label={strings.fields.photoEvidence.label}
          placeholder="foto1.jpg, foto2.jpg"
          value={
            Array.isArray(form.photoEvidence)
              ? form.photoEvidence.join(", ")
              : form.photoEvidence || ""
          }
          onChange={(e) => setForm({ ...form, photoEvidence: e.target.value })}
        />
      </Grid>

      <TextAreaField
        name="observations"
        label={strings.fields.observations.label}
        value={form.observations || ""}
        onChange={(e) => setForm({ ...form, observations: e.target.value })}
      />

      <Flex justifyContent="end">
        <Button
          children={strings.fields.buttons.reset}
          onClick={() => setForm(defaultState)}
        />
        <Button
          children={
            type === "create"
              ? strings.fields.buttons.create
              : strings.fields.buttons.save
          }
          onClick={onSubmitForm}
          variation="primary"
        />
      </Flex>
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      date: { label: "Fecha" },
      farmId: { label: "Finca" },
      equipmentId: { label: "Equipo" },
      serialNumber: { label: "Serial" },
      theoreticalNozzleFlow: { label: "Descarga teórica (cc/min)" },
      actualNozzleFlow: { label: "Descarga real (cc/min)" },
      nozzleCondition: { label: "Estado boquilla" },
      nozzleChange: { label: "Cambio boquilla" },
      performedById: { label: "Persona que realiza" },
      photoEvidence: { label: "Evidencia fotográfica" },
      observations: { label: "Observaciones" },
      createdAt: { label: "Fecha de creación" },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
        cancel: "Cancelar",
      },
    },
  },
});

export default EquipmentCalibrationForm;
